import moment from 'moment';
import { TFunction } from 'i18next';
import { ICompany, ICompanyBuyerDTO } from 'types/Company.types';
import {
  ICustomModelDTO,
  IModel,
  IMyModel,
  IMyModelsDTO,
} from 'types/Model.types';
import { IOrderDTO, IOrderResponseDTO } from 'types/Orders.types';
import { IQuotationResponseDTO, IQuotationDTO } from 'types/Quotation.types';
import { IUser, IUserTableDTO } from 'types/User.types';
import { AppMode } from 'store/Common/reducers/common';
import { formatMoney } from 'utils/moneyFormatter';
import { getOrderListStatus } from 'utils/orderUtils';

export const formatOrdersTableData = (
  ordersData: IOrderResponseDTO[],
  appMode: AppMode
) => {
  return ordersData?.map((order: IOrderResponseDTO) => {
    const deliveryDate =
      order.confirmed_delivery_date || order.requested_delivery_date;
    const orderTableDTO: IOrderDTO = {
      internal_number: order.internal_number,
      number: order.number,
      name:
        appMode === AppMode.BUYER
          ? order.company_seller.name
          : order.company_buyer.name,
      status: getOrderListStatus(order),
      delivery_date: deliveryDate
        ? moment(deliveryDate).format('DD.MM.YYYY')
        : '',
      reference: order.reference,
      created_at: moment(order.created_at).format('DD.MM.YYYY'),
      // connected_quotation: order.quo,
      navigationID: order.id.toString(),
    };
    return orderTableDTO;
  });
};

export const formatQuotationsTableData = (
  quotationsData: IQuotationResponseDTO[]
): IQuotationDTO[] => {
  return quotationsData?.map((quotation: IQuotationResponseDTO) => {
    const quotationTableDTO: IQuotationDTO = {
      number: quotation.number,
      reference: quotation.reference,
      customer: `${quotation.customer_info?.first_name ?? ''} ${
        quotation.customer_info?.last_name ?? ''
      }`.trim(),
      status: quotation.status,
      price: formatMoney(
        quotation.total_net_price,
        quotation.currency || 'EUR'
      ),
      created_at: moment(quotation.created_at).format('DD.MM.YYYY'),
      navigationID: quotation.id.toString(),
    };
    return quotationTableDTO;
  });
};

export const formatCustomModelsTableData = (modelsData: IModel[]) => {
  return modelsData?.map((model: IModel) => {
    const modelTableDTO: ICustomModelDTO = {
      name: model.general.name,
      id: model.id,
      version: model.version_number,
      navigationID: model.id.toString(),
    };
    return modelTableDTO;
  });
};

export const formatBuyersTableData = (companyData: ICompany[]) => {
  return companyData?.map((company: ICompany) => {
    const modelTableDTO: ICompanyBuyerDTO = {
      name: company.name,
      reference_number: company.customer_number,
      navigationID: company.id.toString(),
    };
    return modelTableDTO;
  });
};

export const formatMyModelsTableData = (
  modelsData: IMyModel[],
  t: TFunction
) => {
  return modelsData?.map((model: IMyModel) => {
    const modelTableDTO: IMyModelsDTO = {
      name: model.name || t('Unnamed model'),
      public: model.is_public ? t('Yes') : t('No'),
      category: model.category_name,
      id: model.id,
      version: model.last_version_id,
      creationDate: moment(model.created_at).format('DD/MM/YYYY'),
      deletionDate: model.is_deleted ? t('Yes') : t('No'),
      navigationID: model.id.toString(),
    };
    return modelTableDTO;
  });
};

export const formatUsersTableData = (companyData: IUser[], t: TFunction) => {
  return companyData?.map((user: IUser) => {
    const userTableDTO: IUserTableDTO = {
      navigationID: user.id.toString(),
      name: user.first_name + ' ' + user.last_name,
      email: user.email,
    };
    return userTableDTO;
  });
};
